import { useState, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CircleAlert, MessageSquareWarningIcon, Settings as SettingsIcon } from "lucide-react";

import { NavigationItemType } from "../types";

import { useNavigationActiveState } from "./useNavigationActiveState";

import {
  isNewFeatureModalUnseen,
  NEW_FEATURE_MODAL_SEEN_EVENT,
  openNewFeatureModal,
} from "@/components/NewFeatureModal";
import { useSidebar } from "@/components/ui/sidebar";
import { NAVIGATION } from "@/constants";
import { useUser } from "@/context/user-context";
import { isIntercomHost } from "@/utils/intercom";

export const useNavigationState = (onOpenSettings?: () => void) => {
  const { state } = useSidebar();
  const { user } = useUser();
  const isCollapsed = state === "collapsed";
  const [hasUnseenNewFeatures, setHasUnseenNewFeatures] = useState(() =>
    isNewFeatureModalUnseen(user?.created_at),
  );
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
  const wasCollapsedRef = useRef(isCollapsed);
  const location = useLocation();
  const { isItemActive } = useNavigationActiveState();
  const showContactUs = isIntercomHost(window.location.hostname);

  useEffect(() => {
    const updateUnseenState = () => {
      setHasUnseenNewFeatures(isNewFeatureModalUnseen(user?.created_at));
    };

    updateUnseenState();
    window.addEventListener("storage", updateUnseenState);
    window.addEventListener(NEW_FEATURE_MODAL_SEEN_EVENT, updateUnseenState);

    return () => {
      window.removeEventListener("storage", updateUnseenState);
      window.removeEventListener(NEW_FEATURE_MODAL_SEEN_EVENT, updateUnseenState);
    };
  }, [user?.created_at]);

  // Get navigation items (Quick Links removed - now in User Section)
  const getNavigationItems = useCallback((): NavigationItemType[] => {
    const contactUsNavItem: NavigationItemType = {
      name: "Contact Us",
      icon: MessageSquareWarningIcon,
      onClick: () => {
        // @ts-expect-error Intercom is not typed
        window.Intercom?.("show");
      },
    };

    const settingsNavItem: NavigationItemType = {
      name: "Settings",
      icon: SettingsIcon,
      onClick: onOpenSettings,
    };

    const newFeaturesNavItem: NavigationItemType = {
      name: "What's New",
      icon: CircleAlert,
      onClick: openNewFeatureModal,
      badgeLabel: hasUnseenNewFeatures ? "NEW" : undefined,
      showUnreadIndicator: hasUnseenNewFeatures,
    };

    return [
      ...NAVIGATION,
      ...(showContactUs ? [contactUsNavItem] : []),
      settingsNavItem,
      newFeaturesNavItem,
    ];
  }, [hasUnseenNewFeatures, onOpenSettings, showContactUs]);

  useEffect(() => {
    const navigationItems = getNavigationItems();
    const newOpenMenus = new Set<string>();

    const checkAndOpenActiveMenus = () => {
      navigationItems.forEach(item => {
        if (item.items && item.items.length > 0) {
          const isMainItemActive = isItemActive(item);

          if (isMainItemActive) {
            newOpenMenus.add(item.name);

            item.items.forEach(subItem => {
              if (subItem.items && subItem.items.length > 0) {
                const isSubItemActive = isItemActive(subItem);
                if (isSubItemActive) {
                  newOpenMenus.add(`${item.name}.${subItem.name}`);
                }
              }
            });
          }
        }
      });
    };

    checkAndOpenActiveMenus();
    setOpenMenus(newOpenMenus);
  }, [getNavigationItems, location.pathname, isItemActive]);

  useEffect(() => {
    if (isCollapsed && !wasCollapsedRef.current) {
      setOpenMenus(new Set());
    } else if (!isCollapsed && wasCollapsedRef.current) {
      const navigationItems = getNavigationItems();
      const menusToRestore = new Set<string>();

      navigationItems.forEach(item => {
        if (item.items && item.items.length > 0) {
          const isMainItemActive = isItemActive(item);

          if (isMainItemActive) {
            menusToRestore.add(item.name);

            item.items.forEach(subItem => {
              if (subItem.items && subItem.items.length > 0) {
                const isSubItemActive = isItemActive(subItem);
                if (isSubItemActive) {
                  menusToRestore.add(`${item.name}.${subItem.name}`);
                }
              }
            });
          }
        }
      });

      setOpenMenus(menusToRestore);
    }

    wasCollapsedRef.current = isCollapsed;
  }, [isCollapsed, getNavigationItems, isItemActive]);

  const isMenuOpen = useCallback((menuKey: string): boolean => openMenus.has(menuKey), [openMenus]);

  const toggleMenu = useCallback((menuKey: string, hasSubItems = false) => {
    if (!hasSubItems) {
      return;
    }

    setOpenMenus(prev => {
      const newOpenMenus = new Set(prev);

      if (newOpenMenus.has(menuKey)) {
        newOpenMenus.delete(menuKey);
      } else {
        const menuLevel = menuKey.split(".").length;
        prev.forEach(key => {
          if (key.split(".").length === menuLevel) {
            newOpenMenus.delete(key);
          }
        });
        newOpenMenus.add(menuKey);
      }

      return newOpenMenus;
    });
  }, []);

  return {
    isCollapsed,
    navigationItems: getNavigationItems(),
    isMenuOpen,
    toggleMenu,
  };
};
